import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBars, faTimes, faRightToBracket, faUser } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from './LanguageSwitcher';
import { useAuth } from '../context/AuthContext';
import { isWebViewMode } from '../utils/webviewUtils';
import './Header.css';

const Header = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const { isAuthenticated, logout } = useAuth();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  // Scroll efekti
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Menü kapatma
  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  // Ana sayfa kontrolü
  const isHomePage = location.pathname === '/';
  
  // WebView kontrolü
  const isWebView = isWebViewMode();

  // Bu koyu temalı global header yalnızca eski statik sayfalarda kullanılır.
  // Mağaza, auth ve 404 sayfaları kendi üst barlarını (StoreHeader) render eder.
  const { pathname } = location;
  const darkHeaderPages = ['/about', '/contact', '/privacy', '/soon'];
  const showHeader = darkHeaderPages.includes(pathname);

  const menuItems = [
    { path: '/about', label: t('nav.about') },
    { path: '/contact', label: t('nav.contact') },
    { path: '/privacy', label: t('nav.privacy') }
  ];

  // WebView modunda veya kendi üst barı olan sayfalarda header'ı gizle
  if (isWebView || !showHeader) {
    return null;
  }

  return (
    <header className={`header ${isScrolled ? 'scrolled' : ''} ${isHomePage ? 'homepage' : ''}`}>
      <div className="header-container">
        {/* Logo */}
        <Link to="/" className="logo" onClick={closeMenu}>
          <img src={process.env.PUBLIC_URL + '/logo.jpg'} alt="Utkan eSIM" />
          <span className="logo-text">Utkan eSIM</span>
        </Link>

        {/* Desktop Navigation */}
        <nav className="desktop-nav">
          <ul className="nav-menu">
            {menuItems.map((item) => (
              <li key={item.path}>
                <Link 
                  to={item.path} 
                  className={location.pathname === item.path ? 'active' : ''}
                >
                  {item.label}
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        {/* Language Switcher */}
        <div className="header-language">
          <LanguageSwitcher />
        </div>

        {/* Auth control */}
        <div className="header-auth">
          {isAuthenticated ? (
            <>
              <Link to="/account" className="header-account">
                <FontAwesomeIcon icon={faUser} />
                <span>{t('nav.account')}</span>
              </Link>
              <button type="button" className="header-logout" onClick={logout}>
                {t('nav.logout')}
              </button>
            </>
          ) : (
            <Link to="/login" className="header-login-btn">
              <FontAwesomeIcon icon={faRightToBracket} />
              <span>{t('nav.login')}</span>
            </Link>
          )}
        </div>

        {/* Mobile Menu Button */}
        <button 
          className="mobile-menu-btn"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          aria-label="Toggle menu"
        >
          <FontAwesomeIcon icon={isMenuOpen ? faTimes : faBars} />
        </button>
      </div>

      {/* Mobile Navigation */}
      <nav className={`mobile-nav ${isMenuOpen ? 'open' : ''}`}>
        <ul className="mobile-menu">
          {menuItems.map((item) => (
            <li key={item.path}>
              <Link
                to={item.path}
                className={location.pathname === item.path ? 'active' : ''}
                onClick={closeMenu}
              >
                {item.label}
              </Link>
            </li>
          ))}
          <li className="mobile-auth">
            {isAuthenticated ? (
              <>
                <Link to="/account" onClick={closeMenu}>{t('nav.account')}</Link>
                <button type="button" onClick={() => { closeMenu(); logout(); }}>
                  {t('nav.logout')}
                </button>
              </>
            ) : (
              <Link to="/login" onClick={closeMenu}>{t('nav.login')}</Link>
            )}
          </li>
        </ul>
      </nav>
    </header>
  );
};

export default Header; 